import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { hole, schicke } from '../lib/api'
import { Button, Empty, IconButton, ListItem } from '../ui'

interface Eintrag {
  id: string
  kind: string
  title: string
  body: string | null
  link: string | null
  createdAt: string
  readAt: string | null
}

interface Antwort {
  eintraege: Eintrag[]
  weiter: string | null
}

const zeitpunkt = (iso: string): string =>
  new Date(iso).toLocaleString('de-DE', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * Der eigene Posteingang.
 *
 * ⚠️ Der Titel steht hier NICHT noch einmal zusammengesetzt: anders als beim
 * Aktivitätsfeed liefert der Server fertigen Text, weil R5-B dieselben Zeilen
 * per Push verschickt und ein Handy aus `tasks.completed` keinen deutschen
 * Satz machen kann. Die Seite zeigt, was zugestellt wurde - sonst stünde im
 * Posteingang etwas anderes als auf dem Sperrbildschirm.
 */
export function Posteingang() {
  const qc = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['notifications', 'liste'],
    queryFn: () => hole<Antwort>('/api/notifications'),
  })

  const alleGelesen = useMutation({
    mutationFn: () => schicke<{ markiert: number }>('POST', '/api/notifications/alle-gelesen'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const gelesen = useMutation({
    mutationFn: (id: string) => schicke<{ ok: true }>('POST', `/api/notifications/${id}/gelesen`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const eintraege = data?.eintraege ?? []
  const offen = eintraege.filter((e) => e.readAt === null).length

  return (
    <>
      <div className="seitenkopf">
        <h1>Posteingang</h1>
        {offen > 0 ? (
          <Button
            variant="text"
            onClick={() => {
              alleGelesen.mutate()
            }}
          >
            Alle gelesen
          </Button>
        ) : null}
      </div>

      {isPending ? null : eintraege.length === 0 ? (
        <Empty titel="Nichts Neues" text="Hier ist nichts. Das ist eine gute Nachricht." />
      ) : (
        <ul className="liste liste--gruppe">
          {eintraege.map((e) => (
            <li key={e.id}>
              <ListItem
                as="div"
                titel={e.link ? <Link to={e.link}>{e.title}</Link> : e.title}
                unterzeile={[e.body, zeitpunkt(e.createdAt)].filter(Boolean).join(' · ')}
                // ⚠️ Ungelesene tragen einen PUNKT, keine Farbfläche: eine
                // eingefärbte Zeile liest sich als Warnung, und der
                // Posteingang warnt nicht, er informiert.
                fuehrend={
                  e.readAt === null ? <span className="post-punkt" aria-hidden="true" /> : undefined
                }
                folgend={
                  e.readAt === null ? (
                    <IconButton
                      name="check"
                      // ⚠️ Titel UND Rumpf: der Titel allein ist generisch
                      // („Aufgabe erledigt"), zwei Zeilen hätten damit für eine
                      // Sprachausgabe denselben Namen - und der Knopf wäre
                      // nicht mehr zu unterscheiden.
                      label={`${[e.title, e.body].filter(Boolean).join(': ')} als gelesen markieren`}
                      onClick={() => {
                        gelesen.mutate(e.id)
                      }}
                    />
                  ) : undefined
                }
              />
            </li>
          ))}
        </ul>
      )}

      {data?.weiter ? (
        <p className="hinweis">
          Ältere Einträge stehen noch da. <Link to="/posteingang">Neu laden</Link>
        </p>
      ) : null}
    </>
  )
}
